import React from 'react';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';

/**
 * CollabBadge — trust signals derived from completed Postaway collabs (PRD 4).
 *
 * Variants:
 *   'stamp-inline'    — scalloped verification stamp rendered next to a name.
 *   'stamp-avatar'    — same stamp with a white halo to overlay an image
 *                       corner (parent must be position: relative). Sits
 *                       inset top-right so overflow: hidden wrappers don't
 *                       clip it; pass corner="bottom-right" for the
 *                       overlapping avatar treatment.
 *   'count-pill'      — milestone pill ("10+ Collabs"), navy outline,
 *                       matching MembershipBadge proportions.
 *   'count-highlight' — the selling-point treatment: navy pill announcing
 *                       the exact number of Postaway collabs. Shows at 1+.
 *
 * The stamp is earned, not toggled: 2+ completed collabs, at least one
 * 5-star review, or founder status. Ineligible users simply render
 * nothing — absence of a badge is never presented as a negative state.
 */

export const VERIFIED_MIN_COLLABS = 2;
const MILESTONES = [25, 10];

// Hover copy for the verified stamp (wording provided by Jamie).
export const VERIFIED_STAMP_TOOLTIP =
  'Verified Postaway member. Find out how to earn your stamp by clicking \u2018get verified\u2019 under creators/hosts tab.';

// Scalloped seal shape (lucide "badge-check", https://lucide.dev/icons/badge-check)
// rendered with a solid red fill and white check, per the brand treatment.
const BADGE_PATH = 'M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z';
const CHECK_PATH = 'm9 12 2 2 4-4';

export const StampMark = ({ size, halo = false }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    aria-hidden
    style={{ display: 'block', flexShrink: 0 }}
  >
    {halo && (
      <path d={BADGE_PATH} fill="none" stroke="#ffffff" strokeWidth="5" strokeLinejoin="round" />
    )}
    <path d={BADGE_PATH} fill="#dc1d08" stroke="#dc1d08" strokeWidth="1.5" strokeLinejoin="round" />
    <path
      d={CHECK_PATH}
      fill="none"
      stroke="#ffffff"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// rating_distribution is a jsonb column like {"1": 0, ..., "5": 3} kept in
// sync by review triggers; a nonzero "5" bucket means at least one 5-star review.
export const hasFiveStarReview = (ratingDistribution) => {
  if (!ratingDistribution) return false;
  let dist = ratingDistribution;
  if (typeof dist === 'string') {
    try { dist = JSON.parse(dist); } catch (_) { return false; }
  }
  return (parseInt(dist['5'], 10) || 0) > 0;
};

const CollabBadge = ({
  completedCollabs,
  variant = 'stamp-inline',
  corner = 'top-right',
  isFounder = false,
  ratingDistribution = null,
}) => {
  const count = parseInt(completedCollabs, 10) || 0;
  const fiveStar = hasFiveStarReview(ratingDistribution);
  const isVerified = count >= VERIFIED_MIN_COLLABS || !!isFounder || fiveStar;

  if (variant === 'count-pill') {
    const milestone = MILESTONES.find(m => count >= m);
    if (!milestone) return null;
    return (
      <span
        title={`${count} completed collabs on Postaway`}
        style={{
          padding: '6px 10px',
          borderRadius: '6px',
          fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
          fontSize: '12px',
          fontWeight: '600',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          border: '1.5px solid #0f084b',
          color: '#0f084b',
          backgroundColor: 'transparent',
        }}
      >
        <StampMark size={15} />
        {milestone}+ Collabs
      </span>
    );
  }

  // The selling point: exact collab count, styled to stand out. Shows from
  // the very first completed collab so early users get the flex too.
  if (variant === 'count-highlight') {
    if (count < 1) return null;
    return (
      <span
        title="Collaborations completed through Postaway"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px',
          padding: '7px 14px 7px 10px',
          borderRadius: '100px',
          background: '#0f084b',
          color: '#ffffff',
          fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
          fontSize: '13px',
          fontWeight: 600,
          lineHeight: 1,
          boxShadow: '0 2px 8px rgba(15, 8, 75, 0.25)',
        }}
      >
        <StampMark size={18} />
        <span>
          <strong style={{ fontSize: '15px', fontWeight: 800 }}>{count}</strong>
          {' '}Postaway Collab{count === 1 ? '' : 's'}
        </span>
      </span>
    );
  }

  if (!isVerified) return null;

  if (variant === 'stamp-avatar') {
    const placement = corner === 'bottom-right'
      ? { bottom: '-3px', right: '-3px' }
      : { top: '8px', right: '8px' };
    return (
      <Tippy content={VERIFIED_STAMP_TOOLTIP} placement="top" maxWidth={260}>
        <span
          style={{
            position: 'absolute',
            ...placement,
            zIndex: 2,
            display: 'inline-flex',
            lineHeight: 0,
            filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.25))',
          }}
        >
          <StampMark size={26} halo />
        </span>
      </Tippy>
    );
  }

  // Default: inline by the name.
  return (
    <Tippy content={VERIFIED_STAMP_TOOLTIP} placement="top" maxWidth={260}>
      <span
        style={{ display: 'inline-flex', verticalAlign: 'middle', marginLeft: '6px', lineHeight: 0 }}
      >
        <StampMark size={18} />
      </span>
    </Tippy>
  );
};

/**
 * CollabTrustStats — the trust-signal row for profile modals: the collab
 * count highlight pill plus a rating/review chip. Renders nothing when
 * there's nothing meaningful to show.
 */
export const CollabTrustStats = ({
  completedCollabs,
  averageRating,
  totalReviews,
  align = 'flex-start',
  hideCount = false,
  compact = false,
  reviewsHref = null,
}) => {
  const count = parseInt(completedCollabs, 10) || 0;
  const reviews = parseInt(totalReviews, 10) || 0;
  const rating = averageRating != null ? parseFloat(averageRating) : null;
  const hasRating = reviews > 0 && rating != null && !Number.isNaN(rating);
  const showCount = !hideCount && count >= 1;

  if (!showCount && !hasRating) return null;

  return (
    <div style={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: align,
      flexWrap: 'wrap',
      gap: '8px',
      margin: compact ? 0 : (showCount || hasRating ? '10px 0 2px' : 0),
    }}>
      {showCount && <CollabBadge completedCollabs={count} variant="count-highlight" />}
      {hasRating && (() => {
        const chipStyle = {
          display: 'inline-flex',
          alignItems: 'center',
          gap: '5px',
          padding: '7px 14px',
          borderRadius: '100px',
          background: '#ffffff',
          border: '1.5px solid #e0e0e0',
          color: '#0f084b',
          fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
          fontSize: '13px',
          fontWeight: 600,
          lineHeight: 1,
          textDecoration: 'none',
          cursor: reviewsHref ? 'pointer' : 'default',
        };
        const chipTitle = reviewsHref
          ? `See all ${reviews} review${reviews === 1 ? '' : 's'}`
          : `${rating.toFixed(1)} average across ${reviews} review${reviews === 1 ? '' : 's'}`;
        const chipContent = (
          <>
            <span style={{ color: '#dc1d08', fontSize: '14px' }}>★</span>
            <strong style={{ fontSize: '15px', fontWeight: 800 }}>{rating.toFixed(1)}</strong>
            <span style={{ color: '#6b7280', fontWeight: 500 }}>
              · {reviews} review{reviews === 1 ? '' : 's'}
            </span>
          </>
        );
        return reviewsHref ? (
          <a href={reviewsHref} title={chipTitle} style={chipStyle} onClick={(e) => e.stopPropagation()}>
            {chipContent}
          </a>
        ) : (
          <span title={chipTitle} style={chipStyle}>
            {chipContent}
          </span>
        );
      })()}
    </div>
  );
};

export default CollabBadge;
